import React, { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { formatCurrency } from '@erp/shared-utils';
import { RebatePoint, OverallRebateMetrics } from '@erp/shared-types';
import { getRebateAmount } from '@/utils/rebateAmountSelector';
interface DateRange {
  startDate: Date;
  endDate: Date;
}
interface RebateStatusCardsProps {
  totalOwed: number;
  compact?: boolean; // For smaller cards if needed
  // Enhanced props for summary card style
  summaryStyle?: boolean;
  totalProjected: number;
  currentActualPosition?: RebatePoint | null;
  currentPredictedPosition?: RebatePoint | null;
  forecastPeriod?: DateRange & { totalDays: number; description: string };
  // New props for conditional/non-conditional breakdown
  rebateRules?: Array<{
    id: string;
    name?: string;
    isConditional: boolean;
  }>;
  conditionalRebateYtd?: number;
  nonConditionalRebateYtd?: number;
  isCumulative?: boolean; // Whether to show cumulative (YTD) or month-by-month values
  tagTotals?: Map<string, number>; // Map of tag name -> total amount
  tagNameToIdMap?: Map<string, string>; // Map of tag name -> tag ID
  // Overall metrics for rebate percentage calculations
  overallMetrics?: {
    actual: OverallRebateMetrics;
    predicted: OverallRebateMetrics;
  };
}

const RebateStatusCards: React.FC<RebateStatusCardsProps> = ({
  totalOwed,
  compact = false,
  summaryStyle = false,
  totalProjected,
  currentActualPosition,
  currentPredictedPosition,
  forecastPeriod,
  rebateRules: _rebateRules,
  conditionalRebateYtd,
  nonConditionalRebateYtd,
  isCumulative = true,
  tagTotals,
  tagNameToIdMap: _tagNameToIdMap,
  overallMetrics,
}) => {
  // Get current month name for month-by-month display
  const currentMonthLabel = currentActualPosition
    ? new Intl.DateTimeFormat('en-US', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(currentActualPosition.date)
    : 'Current Month';

  // Determine labels based on cumulative vs month-by-month
  const expectedRebateLabel = isCumulative
    ? 'Expected Rebate YTD'
    : `Expected Rebate ${currentMonthLabel}`;
  const nonConditionalLabel = isCumulative
    ? 'Expected Non-Conditional Rebate YTD'
    : `Expected Non-Conditional Rebate ${currentMonthLabel}`;
  const conditionalLabel = isCumulative
    ? 'Expected Conditional Rebate YTD'
    : `Expected Conditional Rebate ${currentMonthLabel}`;

  const expectedRebateEndLabel = isCumulative
    ? 'Predicted Rebate Term End'
    : `Predicted Rebate for ${currentMonthLabel} End`;

  // Status cards always show cumulative (YTD) values from backend's overallMetrics
  // The backend calculates these using invoice volumes (not n-counted breakdown volumes)
  // and includes the correct rebatePercentage calculation
  const displayMetrics = overallMetrics;

  // Calculate rebateable percentage (rebate / rebateableVolume)
  // rebateableVolume is the sum of line items with productGroupId != null
  const rebateablePercentages = useMemo(() => {
    if (!displayMetrics) return null;

    const actualRebateablePct =
      displayMetrics.actual.rebateableVolume > 0
        ? (displayMetrics.actual.totalRebateAmount /
            displayMetrics.actual.rebateableVolume) *
          100
        : 0;

    const predictedRebateablePct =
      displayMetrics.predicted.rebateableVolume > 0
        ? (displayMetrics.predicted.totalRebateAmount /
            displayMetrics.predicted.rebateableVolume) *
          100
        : 0;

    return {
      actual: actualRebateablePct,
      predicted: predictedRebateablePct,
    };
  }, [displayMetrics]);

  // If using summary style, render enhanced layout in a 2x2 grid
  if (summaryStyle) {
    // Convert tag totals map to array for rendering
    const tagCards = tagTotals
      ? Array.from(tagTotals.entries()).map(([tagName, amount]) => ({
          tagName,
          amount,
        }))
      : [];

    return (
      <div className="space-y-6">
        {/* Filtered Metrics Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-semibold text-muted-foreground px-2">
              Filtered Metrics
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Expected Rebate */}
            <Card className="bg-green-50 border-green-200">
              <CardContent className="text-center p-4">
                <div className="text-xs font-bold text-muted-foreground mb-2">
                  {expectedRebateLabel}
                </div>
                <div className="text-lg font-bold text-black mb-1">
                  {formatCurrency(
                    currentActualPosition
                      ? getRebateAmount(currentActualPosition, isCumulative)
                      : 0,
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {currentActualPosition
                    ? new Intl.DateTimeFormat('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        timeZone: 'UTC',
                      }).format(currentActualPosition.date)
                    : 'Current Period'}
                </div>
              </CardContent>
            </Card>
            {/* Expected Rebate Term End */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="text-center p-4">
                <div className="text-xs font-bold text-muted-foreground mb-2">
                  {expectedRebateEndLabel}
                </div>
                <div className="text-lg font-bold text-black mb-1">
                  {formatCurrency(
                    isCumulative
                      ? totalProjected
                      : currentPredictedPosition
                        ? getRebateAmount(
                            currentPredictedPosition,
                            isCumulative,
                          )
                        : 0,
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {forecastPeriod ? (
                    <>
                      End of{' '}
                      {isCumulative
                        ? new Date(forecastPeriod.endDate).toLocaleDateString(
                            'en-US',
                            {
                              month: 'long',
                              timeZone: 'UTC',
                            },
                          )
                        : new Intl.DateTimeFormat('en-US', {
                            month: 'long',
                            timeZone: 'UTC',
                          }).format(
                            currentPredictedPosition?.date || new Date(),
                          )}
                    </>
                  ) : (
                    'End of Period'
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Expected Non-Conditional Rebate YTD - show if breakdown is available */}
            {conditionalRebateYtd !== undefined &&
            nonConditionalRebateYtd !== undefined ? (
              <Card className="bg-gray-50 border-purple-200">
                <CardContent className="text-center p-4">
                  <div className="text-xs font-bold text-muted-foreground mb-2">
                    {nonConditionalLabel}
                  </div>
                  <div className="text-lg font-bold text-black mb-1">
                    {formatCurrency(nonConditionalRebateYtd)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Volume & Manual Rules
                  </div>
                </CardContent>
              </Card>
            ) : (
              /* Fallback: show placeholder card when breakdown not available */
              <Card className="bg-gray-50 border-gray-200 opacity-50">
                <CardContent className="text-center p-4">
                  <div className="text-xs font-bold text-muted-foreground mb-2">
                    {nonConditionalLabel}
                  </div>
                  <div className="text-lg font-bold text-gray-500 mb-1">
                    N/A
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Rule data needed
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Expected Conditional Rebate YTD */}
            {conditionalRebateYtd !== undefined &&
            nonConditionalRebateYtd !== undefined ? (
              <Card className="bg-gray-50 border-gray-200">
                <CardContent className="text-center p-4">
                  <div className="text-xs font-bold text-muted-foreground mb-2">
                    {conditionalLabel}
                  </div>
                  <div className="text-lg font-bold text-gray-900 mb-1">
                    {formatCurrency(conditionalRebateYtd)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Growth Rules Only
                  </div>
                </CardContent>
              </Card>
            ) : (
              /* Fallback: show placeholder card when breakdown not available */
              <Card className="bg-gray-50 border-gray-200 opacity-50">
                <CardContent className="text-center p-4">
                  <div className="text-xs font-bold text-muted-foreground mb-2">
                    {conditionalLabel}
                  </div>
                  <div className="text-lg font-bold text-gray-500 mb-1">
                    N/A
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Rule data needed
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tag-based cards - also affected by filters */}
            {tagCards.map(({ tagName, amount }) => {
              const tagLabel = isCumulative
                ? `${tagName} Rebate YTD`
                : `${tagName} Rebate for ${currentMonthLabel}`;

              return (
                <Card key={tagName} className="bg-gray-50 border-gray-200">
                  <CardContent className="text-center p-4">
                    <div className="text-xs font-bold text-muted-foreground mb-2">
                      {tagLabel}
                    </div>
                    <div className="text-lg font-bold text-black mb-1">
                      {formatCurrency(amount)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Tagged Rules
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Overall Rebate Percentage Cards - Not Affected by Filters */}
        {displayMetrics && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-semibold text-muted-foreground px-2">
                Overall Metrics (Not Affected by Filters)
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="bg-muted/30 rounded-lg p-4 border-2 border-dashed border-muted-foreground/20">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* YTD Rebate Percentage */}
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-4">
                    <div className="text-xs font-bold text-muted-foreground mb-3 text-center">
                      {isCumulative
                        ? 'Rebate % YTD'
                        : `Rebate % ${currentMonthLabel}`}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {/* All Volume */}
                      <div className="text-center">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                          All Volume
                        </div>
                        <div className="text-lg font-bold text-purple-700">
                          {displayMetrics.actual.rebatePercentage.toFixed(2)}%
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {formatCurrency(
                            displayMetrics.actual.totalRebateAmount,
                          )}{' '}
                          / {formatCurrency(displayMetrics.actual.totalVolume)}
                        </div>
                      </div>
                      {/* Rebateable Volume */}
                      <div className="text-center border-l border-purple-200 pl-4">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                          Rebateable
                        </div>
                        <div className="text-lg font-bold text-purple-900">
                          {rebateablePercentages?.actual.toFixed(2) ?? '0.00'}%
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {formatCurrency(
                            displayMetrics.actual.totalRebateAmount,
                          )}{' '}
                          /{' '}
                          {formatCurrency(
                            displayMetrics.actual.rebateableVolume,
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Predicted Rebate Percentage */}
                <Card className="bg-indigo-50 border-indigo-200">
                  <CardContent className="p-4">
                    <div className="text-xs font-bold text-muted-foreground mb-3 text-center">
                      {isCumulative
                        ? 'Predicted Rebate % Term End'
                        : `Predicted Rebate % ${currentMonthLabel} End`}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {/* All Volume */}
                      <div className="text-center">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                          All Volume
                        </div>
                        <div className="text-lg font-bold text-indigo-700">
                          {displayMetrics.predicted.rebatePercentage.toFixed(2)}
                          %
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {formatCurrency(
                            displayMetrics.predicted.totalRebateAmount,
                          )}{' '}
                          /{' '}
                          {formatCurrency(displayMetrics.predicted.totalVolume)}
                        </div>
                      </div>
                      {/* Rebateable Volume */}
                      <div className="text-center border-l border-indigo-200 pl-4">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                          Rebateable
                        </div>
                        <div className="text-lg font-bold text-indigo-900">
                          {rebateablePercentages?.predicted.toFixed(2) ??
                            '0.00'}
                          %
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {formatCurrency(
                            displayMetrics.predicted.totalRebateAmount,
                          )}{' '}
                          /{' '}
                          {formatCurrency(
                            displayMetrics.predicted.rebateableVolume,
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Original style
  const cardPadding = compact ? 'p-4' : 'p-6';
  const titleSize = compact ? 'text-lg' : 'text-2xl';
  const subtitleSize = compact ? 'text-sm' : 'text-lg';

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Total Owed */}
      <Card className={compact ? '' : 'bg-purple-600 border-purple-700'}>
        <CardContent className={`text-center ${cardPadding}`}>
          <div
            className={`${subtitleSize} font-medium mb-2 ${
              compact ? 'text-green-600' : 'text-white'
            }`}
          >
            {expectedRebateLabel}
          </div>
          <div
            className={`${titleSize} font-bold ${
              compact ? 'text-blue-600' : 'text-white'
            }`}
          >
            {formatCurrency(totalOwed)}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RebateStatusCards;
